import { useCallback, useEffect, useRef, useState } from 'react';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogActions, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import IdentityCheckStep from '../sections/@dashboard/general/app/IdentityCheckStep';
import { registerIdentityConflictHandler } from '../utils/leadIdentityConflictBus';
import { restoreLeadSync } from '../utils/leadIdentityApi';
import { useSelector } from '../redux/store';

/**
 * Compact "Before you continue" dialog for create surfaces that have no wizard
 * (call script, call-log, WhatsApp / email composers, stale tabs). A create thunk
 * that gets 409 `lead_identity_match` awaits this dialog through
 * leadIdentityConflictBus and retries with the chosen `identity_resolution`.
 */
function ConflictDialog() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const [pending, setPending] = useState(null); // { check }
    const resolverRef = useRef(null);

    useEffect(() => registerIdentityConflictHandler((check) => new Promise((resolve) => {
        // A second conflict while one is open cancels the older request.
        resolverRef.current?.({ cancelled: true });
        resolverRef.current = resolve;
        setPending({ check });
    })), []);

    const finish = useCallback((result) => {
        resolverRef.current?.(result);
        resolverRef.current = null;
        setPending(null);
    }, []);

    const openLead = useCallback((lead) => {
        finish({ cancelled: true, openedLeadId: lead.id });
        navigate(`/dashboard/practice/${practiceId}/lead-detail/${lead.id}`);
    }, [finish, navigate, practiceId]);

    const restore = useCallback(async (lead) => {
        const res = await restoreLeadSync(practiceId, lead.id);
        if (res.ok) {
            enqueueSnackbar('Lead restored.', { variant: 'success' });
            finish({ cancelled: true, restoredLeadId: lead.id });
            navigate(`/dashboard/practice/${practiceId}/lead-detail/${lead.id}`);
        }
        return res;
    }, [practiceId, enqueueSnackbar, finish, navigate]);

    return (
        <Dialog open={Boolean(pending)} onClose={() => finish({ cancelled: true })} maxWidth='sm' fullWidth>
            <DialogContent sx={{ pt: 3 }}>
                {pending && (
                    <IdentityCheckStep
                        compact
                        check={pending.check}
                        onCreateNew={(resolution) => finish({ resolution })}
                        onOpenLead={openLead}
                        onRestore={restore}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button color='inherit' size='small' onClick={() => finish({ cancelled: true })}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// The dialog needs the router to open a lead; outside one (isolated tests) it renders nothing.
export default function LeadIdentityConflictDialog() {
    return useInRouterContext() ? <ConflictDialog /> : null;
}
